import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, OnInit, signal, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Table } from 'primeng/table';
import { UntypedFormGroup } from '@angular/forms';
import { DecimalPipe } from '@angular/common';

import { ConfirmationModalComponent } from "app/shared/form-elements/components/confirmation-modal/confirmation-modal.component";
import { BAService } from 'app/ba/ba.service';
import { BA_ProgramVm } from 'app/shared/generated/BA/Models/Admin/BA_ProgramVm';
import { GlobalService } from 'app/shared/services/global.service';
import { SharedTableModule } from 'app/shared/standalone/shared-table/shared-table.module';
import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { PermissionRole } from 'app/core/enums/generated/PermissionRole';
import { SecurityService } from 'app/core/security/security.service';
import { NotificationService } from 'app/notification.service';
import { SystemMessageService } from 'app/core/system-message/system-message-service';
import { validateForm } from 'app/shared/form-elements/form-validateForm.function';
import { AvailableColumns, VisibleColsService } from 'app/shared/standalone/shared-table/visible-cols.service';
import { LazyTableInput } from 'app/shared/standalone/shared-table/lazy-table-input.interface';
import { LazyTableService } from 'app/shared/standalone/shared-table/lazy-table.service';
import { LazyLoadVm } from 'app/shared/standalone/shared-table/lazy-load-vm.interface';

@Component({
    selector: 'pcg-ba-programs-list'
    , templateUrl: './ba-programs-list.component.html'
    , styleUrls: ['./ba-programs-list.component.scss']
    , standalone: true
    , providers: [ DecimalPipe ]
    , host: { '(window:resize)': 'onResize()' }
    , changeDetection: ChangeDetectionStrategy.Eager
    , imports: [
        SharedTableModule
    ]
})
export class BaProgramsListComponent implements OnInit {

    @ViewChild("modalProgram") modalProgram: TemplateRef<any>;

    httpClient = inject(HttpClient);
    modalService = inject(NgbModal);
    sec = inject(SecurityService);
    ns = inject(NotificationService);
    sm = inject(SystemMessageService);
    baService = inject(BAService);
    decimalPipe = inject(DecimalPipe);
    lts = inject(LazyTableService);
    cdr = inject(ChangeDetectorRef);

    columnDefs = BA_ProgramVm.ColumnDefs;
    lazyLoadVm: LazyLoadVm;
    list: BA_ProgramVm[] = [];
    availableColumns!: AvailableColumns[];
    visibleColumns!: AvailableColumns[];
    searchColumns!: string[];    
    formGroup = BA_ProgramVm.Form;

    canDelete = signal<boolean>(this.sec.hasModuleAccess([ BusinessAreaEnum.BA ], [ PermissionRole.SystemAdmin ]));
    isMobile = false;
    isLoading: boolean = true;
    searchValue: string | undefined;
    modal: NgbModalRef;

    filters = { isActive: true || 'true' };
	initFilters = Object.assign({}, this.filters);

    tableDts = {
        root: {
            header: {
                lgPadding: "0 0 0.5rem 0"
            }
        }
    };

    lazyTableInput: LazyTableInput = {
        pageNumber: 1
        , pageSize: 100
        , sortField: "name"
        , sortOrder: 1
    };

    constructor(title: Title) { title.setTitle("Budget Programs"); }

    onResize() { this.isMobile = GlobalService.setIsMobile(window.innerWidth); }

    ngOnInit() {
        this.isMobile = GlobalService.setIsMobile(window.innerWidth); 
        this.getList();
    }

    getList() { 
        this.httpClient.get(`api/BA/BA_Program/GetList` 
            + `?isActive=${this.filters.isActive}`
        ).subscribe((lst: BA_ProgramVm[]) => { 
            this.list = lst;
            this.availableColumns = VisibleColsService.getAvailableCols(this.columnDefs);
            this.visibleColumns = this.availableColumns.filter(o => o.isVisible);
            this.searchColumns = this.availableColumns.filter(o => o.canSearch).map(o => o.field);
            this.isLoading = false;
        }); 
    }

    columnChange(event: any) {
        this.visibleColumns = event;
        this.visibleColumns = event.sort((a, b) => {
            return this.availableColumns.findIndex(p => p.field === a.field) 
                - this.availableColumns.findIndex(p => p.field === b.field);
        });
    }

    editProgram(row: BA_ProgramVm) {
        this.formGroup = BA_ProgramVm.Form;
        if (row != null) {
            this.formGroup.controls.id.setValue(row.id);
            this.formGroup.controls.name.setValue(row.name);
            this.formGroup.controls.isActive.setValue(row.isActive);
        } else {
            this.formGroup.controls.id.setValue(0);
            this.formGroup.controls.name.setValue("");
            this.formGroup.controls.isActive.setValue(true);
        }

        this.modalService.open(this.modalProgram, { animation: false }); 
    }

    clearFilters(
        table: Table
        , el: any
    ) {
        table.clear();
        this.searchValue = '';
        el.value = "";
        this.filters = Object.assign(this.filters, this.initFilters);
        this.getList();
    }

    deleteProgram(id?: number) {
        this.modal = this.modalService.open(ConfirmationModalComponent);
        this.modal.componentInstance.modalRef = this.modal;
        this.modal.componentInstance.confirmationMessage = "Are you sure you want to delete this B&A Program?";
        this.modal.componentInstance.isMobile = this.isMobile;
        
        this.modal.result.then((emittedValue) => {
            if (emittedValue) {
                this.sm.getHttpObservable(this,`api/BA/BA_Program/Delete/${id}`)
                    .subscribe((sm: any) => {
                        if (sm.isSuccessful) {
                            this.ns.showSuccess(sm.message);
                            this.getList();
                        } else {
                            this.ns.showError(sm.message);
                        }
                    }
                );
            }
        }).catch((err) => {
            // Prevent Uncaught (in promise) error
        });
    }

    saveProgram(
        modal: { dismiss: () => void; }
        , form: UntypedFormGroup
    ) {
        if (form.valid) {
            this.sm.getHttpObservable(this, 'api/BA/BA_Program/Save', this.formGroup)
                .subscribe(msg => {
                    if (msg.isSuccessful) {
                        modal.dismiss();
                        this.getList();
                    }
                }
            );
        } else { validateForm(form); }
    }

    toggleIsActive(
        modal: { dismiss: () => void; }
        , id: number
    ) {
        this.sm.getHttpObservable(this, `api/BA/BA_Program/ActivateDeactivate/${id}`)
            .subscribe((sm: any) => {
                if (sm.isSuccessful) {
                    this.ns.showSuccess(sm.message);
                    modal.dismiss();
                    this.getList();
                } else {
                    this.ns.showError(sm.message);
                }
            }
        );
    };
}
